import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, RefreshCw, Search as SearchIcon, X } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import { useAccount } from '../contexts/AccountContext';
import { formatDateTime } from '../utils/datetime';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import CopyButton from './shared/CopyButton';
import UapiTraceSteps from './shared/UapiTraceSteps';
import IntegrationLogTable from './shared/IntegrationLogTable';
import './UniversalApiPage.css';

// Universal API > Logs — what this property has actually called, and what happened.
//
// ONE ROW PER REQUEST. The trace index holds a document per DECISION (arrived, authorized, resolved,
// granted, redaction, backend, responded), so seven rows per call; the server folds them and this
// renders the fold. Expanding a row fetches that transaction's steps from the endpoint the test console
// already uses — the same evidence, not a second version of it.
//
// SCOPED BY THE HOTEL PICKER, like every other screen in this section. With no hotel there is nothing
// to show: a trace belongs to a property, and "all properties" is a question this screen is not asked.
//
// THREE DAYS. `uapi-trace-*` rolls daily with an ISM delete at 3d, so this is recent activity and not
// an audit trail. The empty state says so in words, because "no logs" and "the logs expired" look
// identical and only one of them is a problem.

// Outcome -> the chip it is drawn as. Keys are what the dispatcher writes.
const OUTCOME_CLASS = { ok: 'lu-log-ok', denied: 'lu-log-denied', error: 'lu-log-error' };

const statusClass = (status, outcome) => {
    if (OUTCOME_CLASS[outcome]) return OUTCOME_CLASS[outcome];
    if (!status) return 'lu-log-unknown';
    if (status < 300) return 'lu-log-ok';
    if (status < 500) return 'lu-log-denied';
    return 'lu-log-error';
};

const UapiLogsTab = () => {
    const { selectedHotel } = useAccount();

    const [rows, setRows] = useState([]);
    // The integration log's steps for this hotel that involve the Universal API (touches_uapi), returned with
    // the traces — same property, same window, same search.
    const [integrationGroups, setIntegrationGroups] = useState([]);
    const [integrationError, setIntegrationError] = useState(null);
    const [note, setNote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // The property's bindings, which is what a credential means on this screen: each carries its own
    // credential and its own x-hotelid, and a hotel can hold several to one system.
    const [bindings, setBindings] = useState([]);
    const [accessId, setAccessId] = useState('');

    // Free text. `search` is what the user is typing; `applied` is what the server was last asked for.
    // Separated because the search runs against the SERVER — it reads the bodies, which the browser does
    // not hold — so it cannot fire on every keystroke.
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState('');

    const [openId, setOpenId] = useState(null);
    const [steps, setSteps] = useState({});      // transactionId -> steps[]
    const [stepsLoading, setStepsLoading] = useState(null);

    const load = useCallback(async () => {
        if (!selectedHotel) { setRows([]); return; }
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.listTraces({
                hotelId: selectedHotel,
                accessId: accessId || undefined,
                q: applied || undefined,
            });
            const data = res?.data || res;
            setRows(data?.requests || []);
            setIntegrationGroups(data?.integrationGroups || []);
            setIntegrationError(data?.integrationError || null);
            setNote(data?.note || null);
        } catch (e) {
            setError(e?.response?.data?.error?.message || e?.message || 'Could not load the logs');
            setRows([]);
            setIntegrationGroups([]);
        } finally {
            setLoading(false);
        }
    }, [selectedHotel, accessId, applied]);

    useEffect(() => { load(); }, [load]);

    // The filter's options. From the DATABASE, not from the logs: a credential with no traffic yet must
    // still be selectable, or the reader cannot tell "this credential is quiet" from "this credential
    // does not exist" — and the first is the more common question.
    useEffect(() => {
        if (!selectedHotel) { setBindings([]); return undefined; }
        let cancelled = false;
        legacyUapiService.listAccess(selectedHotel)
            // { access: [...] } — the same unwrap UapiAccessDetailPage uses for this call.
            .then((res) => { if (!cancelled) setBindings(((res?.data || res)?.access) || []); })
            .catch(() => { if (!cancelled) setBindings([]); });
        return () => { cancelled = true; };
    }, [selectedHotel]);

    const toggle = async (transactionId) => {
        if (openId === transactionId) { setOpenId(null); return; }
        setOpenId(transactionId);
        if (steps[transactionId]) return;
        try {
            setStepsLoading(transactionId);
            const res = await legacyUapiService.getTrace(transactionId);
            const data = res?.data || res;
            setSteps(prev => ({ ...prev, [transactionId]: data?.steps || [] }));
        } catch (e) {
            setSteps(prev => ({ ...prev, [transactionId]: [] }));
        } finally {
            setStepsLoading(null);
        }
    };

    if (!selectedHotel) {
        return (
            <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                    Select a hotel to see its Universal API logs.
                </p>
                <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '52ch' }}>
                    Use the hotel picker in the top bar. A request is traced against the property its
                    x-hotelid resolved to.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="lu-log-controls">
                {/* A BINDING filter, not a credential filter — it narrows on access_id, which is one
                    x-hotelid routing to one backend. It was labelled "Credential" and listed the
                    binding's BACKEND credential (what the hub presents to OHIP), while the table's
                    Caller column shows the INBOUND credential the trace records (what the consumer
                    presented to us). Two different credentials, in two adjacent controls, both called
                    "credential" — so they could never agree and the mismatch looked like a bug in the
                    data. Named for what it selects, and identified by the x-hotelid, which is the thing
                    that is actually unique per binding. */}
                <label className="lu-log-filter">
                    <span className="lu-log-filter-label">Binding (x-hotelid)</span>
                    <select
                        className="ppw-select"
                        value={accessId}
                        onChange={(e) => setAccessId(e.target.value)}
                    >
                        <option value="">All bindings</option>
                        {bindings.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.label || b.system_name}
                                {b.uapi_hotel_id ? ` · …${String(b.uapi_hotel_id).slice(-12)}` : ''}
                            </option>
                        ))}
                    </select>
                </label>
                {/* Submitted, not live. The server reads every step's bodies to answer this, so firing
                    on each keystroke would be a request per character against documents that can be
                    100KB each. Enter or the button; Escape or the × clears and reloads. */}
                <label className="lu-log-filter lu-log-search">
                    <span className="lu-log-filter-label">Search</span>
                    <div className="uapi-search-row">
                        <SearchIcon size={14} className="uapi-search-icon" />
                        <input
                            type="text"
                            className="pst-hotel-search"
                            value={search}
                            placeholder="Anything — a path, an error, a guest name, a field that was pruned…"
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') setApplied(search.trim());
                                if (e.key === 'Escape') { setSearch(''); setApplied(''); }
                            }}
                        />
                        {(search || applied) && (
                            <button
                                type="button"
                                className="lu-log-search-clear"
                                onClick={() => { setSearch(''); setApplied(''); }}
                                title="Clear the search"
                            >
                                <X size={13} />
                            </button>
                        )}
                    </div>
                </label>
                <Button
                    variant="secondary"
                    onClick={() => setApplied(search.trim())}
                    disabled={loading || search.trim() === applied}
                >
                    <SearchIcon className="icon" /> Search
                </Button>
                <Button variant="success" onClick={load} disabled={loading}>
                    <RefreshCw className="icon" /> Refresh
                </Button>
            </div>

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {!loading && rows.length > 0 && applied && (
                <p className="lu-log-result-note">
                    {rows.length} request{rows.length === 1 ? '' : 's'} matching
                    {' '}<strong>{applied}</strong> — searched across the call, the step messages, the
                    pruned and rejected field names and the request/response bodies.
                </p>
            )}

            {loading ? <Spinner /> : rows.length === 0 ? (
                <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>No requests to show.</p>
                    <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '56ch' }}>
                        {note || 'Nothing has been called with this property’s x-hotelid recently.'}
                    </p>
                </div>
            ) : (
                <div className="otx-table-wrap">
                    <table className="otx-table lu-log-table">
                        <thead>
                            <tr>
                                <th style={{ width: 32 }} aria-label="Expand" />
                                <th>When</th>
                                <th>Call</th>
                                <th>Result</th>
                                <th>System</th>
                                <th>Caller</th>
                                <th>Transaction</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <React.Fragment key={r.transactionId}>
                                    <tr className="uapi-row-link" onClick={() => toggle(r.transactionId)}>
                                        <td>{openId === r.transactionId
                                            ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                                        <td>{formatDateTime(r.at)}</td>
                                        <td>
                                            <code className="lu-log-call">
                                                {r.method || '—'} {r.path || `${r.routePath || ''}${r.operationPath || ''}` || '—'}
                                            </code>
                                        </td>
                                        <td>
                                            <span className={`lu-log-chip ${statusClass(r.status, r.outcome)}`}>
                                                {r.status || r.outcome || 'unknown'}
                                            </span>
                                            {/* The reason lives on the step that refused, and it is the
                                                whole point of the row when there is one. */}
                                            {r.message && <div className="lu-log-message">{r.message}</div>}
                                        </td>
                                        <td>
                                            {r.systemName || '—'}
                                            {r.xHotelId && (
                                                <div className="lu-log-sub">…{String(r.xHotelId).slice(-12)}</div>
                                            )}
                                        </td>
                                        {/* The credential says WHO; the address says where from. Under
                                            the name rather than in its own column: they answer one
                                            question together, and a credential appearing from an
                                            address nobody recognises is the thing worth spotting. */}
                                        <td>
                                            {r.credentialName || '—'}
                                            {r.sourceIp && (
                                                <div className="lu-log-sub">
                                                    <code>{r.sourceIp}</code>
                                                </div>
                                            )}
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className="uapi-xhotel-cell">
                                                <code className="uapi-xhotel">{r.transactionId}</code>
                                                <CopyButton value={r.transactionId} label="Copy" className="copy-btn-inline" />
                                            </div>
                                            {/* SENT BY AN INTEGRATION: its own transaction id, which is the
                                                join key to the integration steps listed below. */}
                                            {r.correlationId && (
                                                <div className="lu-log-sub" title="The caller's x-correlation-id — the integration's transaction id">
                                                    from <code>{r.correlationId}</code>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    {openId === r.transactionId && (
                                        <tr className="lu-log-steps-row">
                                            <td colSpan={7}>
                                                {/* THE SAME RENDERER THE TEST CONSOLE USES. This was a
                                                    four-column table — step, when, outcome, detail —
                                                    which showed none of the redaction set, the grant,
                                                    the integration version, the entry/exit bodies or
                                                    the diff. Same documents, two renderers, one of them
                                                    much poorer; there is now one. */}
                                                {stepsLoading === r.transactionId ? <Spinner /> : (
                                                    (steps[r.transactionId] || []).length === 0
                                                        ? <p className="lu-muted" style={{ margin: 0 }}>
                                                            No steps — this request&rsquo;s trace has aged out.
                                                          </p>
                                                        : <UapiTraceSteps steps={steps[r.transactionId]} />
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* INTEGRATION ACTIVITY INVOLVING THE UNIVERSAL API — only those steps, not the whole integration
                log: an integration routing this property's messages to the Universal API, or the Universal API
                queueing a write for an integration's OXI. Every integration step is on Integrate Systems > Logs. */}
            {!loading && (
                <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', color: '#111827' }}>Integration activity</h3>
                    <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>
                        Integration steps for this property that call, or are called by, the Universal API. A request above
                        sent by an integration shows its transaction id under “from”.
                    </p>
                    {integrationError && (
                        <p style={{ margin: '0 0 0.75rem', color: '#b91c1c', fontSize: '0.85rem' }}>{integrationError}</p>
                    )}
                    <IntegrationLogTable
                        groups={integrationGroups}
                        emptyText="No integration has exchanged messages with the Universal API for this property in the last 3 days."
                        // Opened lazily through this screen's own endpoint — same property, Universal API steps only.
                        loadSteps={async (groupId) => {
                            const res = await legacyUapiService.getIntegrationLogGroup(selectedHotel, groupId);
                            return ((res?.data || res)?.steps) || [];
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default UapiLogsTab;
